import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { environment } from '@ups/xplat/core';
import { HelpAnchorInfo, HelpDocumentDto } from '@ups/xplat/base/in-app-help';

export interface AskRynConversation {
  Id: string;
  UserId: string;
  AppId: string;
  Title: string;
  /** Who set the current title: 'auto-first-message' | 'summary-ryn' | 'user'.
   * A 'user' title is sovereign (agents never overwrite it). Null/absent on
   * rows that predate titling. */
  TitleSource?: string | null;
  CreatedAt: string;
  UpdatedAt: string;
  Members?: readonly string[];
}

export interface AskRynMembersResult {
  conversationId: string;
  members: string[];
}

/**
 * Optional structured action a Ryn reply can carry alongside its human text.
 * The chat surfaces it via AskRynUiService.pendingAction; the in-app-help wren
 * consumes it (so the dependency stays one-way: in-app-help → ask-ryn, never
 * back). `kind` drives the consumer's switch; extra kinds just add fields.
 *  - fly: dispatch the wren to fly to / spotlight the element at `helpPath`
 *  - walkthrough: start the SAVED walkthrough with id `walkthroughId` at step 0
 *  - playTour: play an in-memory tour authored on-request by a Ryn from the
 *    page's anchor inventory. Carries the whole `document` inline (no DB row,
 *    no id), and the in-app-help wren plays it through the same driver.js player
 *    via HelpActions.playWalkthrough. This is the "default is on request" path.
 *  - reportSpec: a data-free custom report spec in `document` (WI 35286). Only the
 *    Operations Management custom report page reads it, from its OWN conversation;
 *    the shared panel and the wren ignore this kind. The page validates `document`
 *    as untrusted input (parseReportSpec), so the HelpDocumentDto type does not apply.
 *  - gridCommands: Kendo-style grid commands in `document` (US 35406). The Ask Ryn
 *    panel hands them to AskRynGridRegistry (libs/xplat/web/ask-ryn), which validates
 *    them against the registered grid's catalog; the wren never sees this kind.
 */
export interface AskRynAction {
  kind: 'fly' | 'walkthrough' | 'playTour' | 'reportSpec' | 'gridCommands';
  helpPath?: string;
  walkthroughId?: number;
  /** The inline tour to play, present only when kind === 'playTour'. */
  document?: HelpDocumentDto;
}

export interface AskRynMessage {
  Id: string;
  ConversationId: string;
  Role: 'user' | 'assistant';
  Content: string;
  PageUrl?: string;
  AppName?: string;
  Timestamp: string;
  /** Optional structured UI action attached to an assistant reply. */
  Action?: AskRynAction;
  /** Server-attributed seat identity. Missing on legacy or human messages. */
  SenderInstanceId?: string | null;
  /** Opaque conversation-scoped replay cursor; echo verbatim, never sort/decode. */
  Cursor?: string | null;
}

/**
 * A chart a Ryn reply can render inline in the chat. Carried NOT as a separate
 * message field but embedded in the reply's `Content` as a fenced code block:
 *
 *   ```chart
 *   { "type": "column", "title": "Quotes by Month",
 *     "categories": ["Jan","Feb","Mar"], "series": [{ "name": "Quotes", "data": [12,19,7] }] }
 *   ```
 *
 * The frontend extracts the block, renders the surrounding prose as Markdown and
 * the block as a Kendo chart. Riding `Content` means no bridge/DTO change; a
 * responder just writes the fence. `categories` is the shared x-axis (omit for a
 * pie, whose slice labels come from `categories` paired with `series[0].data`).
 */
export interface AskRynChartSpec {
  type: 'column' | 'bar' | 'line' | 'area' | 'pie';
  title?: string;
  categories?: string[];
  series: { name?: string; data: number[] }[];
}

export interface AskRynContext {
  userId: string;
  appId: string;
  pageUrl: string;
  appName: string;
  /** The logged-in user's display name (e.g. "Jane Foreman"), sent so the
   * Ask Ryn -> Teams mirror can show WHO asked instead of "A Sparta user".
   * Optional: when absent the bridge falls back to the generic label. */
  userDisplayName?: string;
  pageHelpContent?: string;
  technicalHelpContent?: string;
  /** Inventory of the current page's help-path anchors, so a responder can
   * author an on-request tour that only targets elements that actually exist
   * on the page (the grounding for the 'playTour' action). */
  pageAnchors?: HelpAnchorInfo[];
}

const BRIDGE_BASE_URL = environment.urls?.bridgeAPI;

@Injectable({ providedIn: 'root' })
export class AskRynService {
  constructor(private readonly http: HttpClient) {}

  getConversations(
    userId: string,
    appId: string
  ): Observable<AskRynConversation[]> {
    const params = new HttpParams().set('userId', userId).set('appId', appId);
    return this.http.get<AskRynConversation[]>(
      `${BRIDGE_BASE_URL}/conversations`,
      { params }
    );
  }

  createConversation(
    userId: string,
    appId: string,
    title?: string
  ): Observable<AskRynConversation> {
    return this.http.post<AskRynConversation>(
      `${BRIDGE_BASE_URL}/conversations`,
      { userId, appId, title }
    );
  }

  getMessages(
    conversationId: string,
    appId: string,
    sinceCursor?: string
  ): Observable<AskRynMessage[]> {
    // appId lets the bridge verify the signed-in user owns this conversation
    // (per-user authorization) before returning its messages.
    let params = new HttpParams().set('appId', appId);
    // Opaque server cursor only; never derive it from a JS timestamp/message Id.
    if (sinceCursor) params = params.set('sinceCursor', sinceCursor);
    return this.http.get<AskRynMessage[]>(
      `${BRIDGE_BASE_URL}/conversations/${encodeURIComponent(conversationId)}/messages`,
      { params }
    );
  }

  /** Owner-only replace, not append. Requires the reviewed 31648 server contract. */
  setMembers(
    conversationId: string,
    appId: string,
    members: readonly string[]
  ): Observable<AskRynMembersResult> {
    const normalized = [...new Set(members.map((id) => id.trim()))];
    if (
      !conversationId.trim() ||
      !appId.trim() ||
      normalized.some((id) => !id) ||
      normalized.length > 20
    ) {
      return throwError(
        () => new Error('A conversation supports at most 20 named seats')
      );
    }
    return this.http.put<AskRynMembersResult>(
      `${BRIDGE_BASE_URL}/conversations/${encodeURIComponent(conversationId)}/members`,
      // The authenticated principal determines ownership; no client role/identity override.
      { appId, members: normalized }
    );
  }

  sendMessage(
    conversationId: string,
    content: string,
    context: AskRynContext
  ): Observable<AskRynMessage> {
    return this.http.post<AskRynMessage>(
      `${BRIDGE_BASE_URL}/conversations/${conversationId}/messages`,
      {
        content,
        role: 'user',
        userId: context.userId,
        // appId is the partition key half the bridge needs to find the
        // conversation row; appName alone (the sidebar name) misses it, which
        // is what silently dropped titles and UpdatedAt (US 30685).
        appId: context.appId,
        appName: context.appName,
        userDisplayName: context.userDisplayName,
        pageUrl: context.pageUrl,
        pageHelpContent: context.pageHelpContent,
        technicalHelpContent: context.technicalHelpContent,
        pageAnchors: context.pageAnchors,
      }
    );
  }

  /** Rename a conversation. The bridge records it as a user-sovereign title
   * (TitleSource 'user'), which agents never overwrite. */
  renameConversation(
    conversationId: string,
    userId: string,
    appId: string,
    title: string
  ): Observable<AskRynConversation> {
    return this.http.patch<AskRynConversation>(
      `${BRIDGE_BASE_URL}/conversations/${conversationId}/title`,
      { userId, appId, title }
    );
  }
}
